import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import arCommon from '@/locales/ar/common.json';
import enCommon from '@/locales/en/common.json';
import arHourly from '@/locales/ar/hourly.json';
import enHourly from '@/locales/en/hourly.json';
import arAuth from '@/locales/ar/auth.json';
import enAuth from '@/locales/en/auth.json';
import arBookings from '@/locales/ar/bookings.json';
import enBookings from '@/locales/en/bookings.json';
import arAccount from '@/locales/ar/account.json';
import enAccount from '@/locales/en/account.json';

export type Lang = 'ar' | 'en';

export const SUPPORTED_LANGS: readonly Lang[] = ['ar', 'en'] as const;

/**
 * i18next bootstrap for the Luvelle customer web app.
 *
 * Mirrors the Flutter app's l10n posture: Arabic is the default locale
 * and RTL is the document direction. English is supported and switches
 * `<html dir>` + Tailwind directional utilities.
 *
 * Namespaces follow the Flutter feature split (`common`, `hourly`,
 * `lead`, etc.); per-feature JSON files keep PR diffs scoped.
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { common: arCommon, hourly: arHourly, auth: arAuth, bookings: arBookings, account: arAccount },
      en: { common: enCommon, hourly: enHourly, auth: enAuth, bookings: enBookings, account: enAccount },
    },
    fallbackLng: 'ar',
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    ns: ['common', 'hourly', 'auth', 'bookings', 'account'],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['cookie', 'querystring', 'navigator'],
      lookupCookie: 'luvelle_lang',
      lookupQuerystring: 'lang',
      caches: ['cookie'],
    },
  });

export function setLanguage(lang: Lang) {
  i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
}

/** Apply the document direction on first paint, before React mounts. */
export function applyDirOnLoad() {
  const initial = (i18n.language?.split('-')[0] || 'ar') as Lang;
  document.documentElement.lang = initial;
  document.documentElement.dir = initial === 'ar' ? 'rtl' : 'ltr';
}

export function otherLang(lang: Lang): Lang {
  return lang === 'ar' ? 'en' : 'ar';
}

export default i18n;
